<template>
  <div class="metric-studio">
    <div class="layout-grid">
      <TopBar
        @clickSave="toggleConsoleDetailsPopupWithProps"
        @clickSearch="toggleCitySearch"
      />
      <div class="content-layout">
        <MetricStudioSidePanel />
        <div class="display-area">
          <MapView
            :geoData="mapData"
            :popupData="getPopupData"
            :mapStyle="getMapStyle"
            :staticLayers="getActiveStaticLayers"
            type="primary"
          />
          <i
            :class="[
              'material-icons',
              'toggle-button',
              'timeline',
              'smooth-shadow',
              { hide: showBottomPanel },
            ]"
            @click="toggleBottomPanel"
            >timeline</i
          >

          <i
            :class="[
              'material-icons',
              'toggle-button',
              'filters',
              'smooth-shadow',
              { hide: isFiltersVisible },
            ]"
            @click="toggleFilterManager()"
            >filter_list</i
          >

          <MetricStudioBottomPanel
            @change="requestWidgetDataForTime"
            @changedTimeFilter="requestWidgetDataForWindow"
          />
          <LegendView
            :title="''"
            :legend="primaryLegend"
            :enableFilter="true"
            type="primary"
          />
          <ZoomControls :compareView="false" />
          <FilterManager
            v-if="getActiveEntity"
            @change="requestForCategoricalFilters"
            @toggle="requestForAppliedFilter"
          />
        </div>
      </div>
    </div>

    <!-- <DateFrequencyPicker @change="requestWidgetDataForWindow" /> -->
    <ComputedPropertyPopup />
    <SlideYUpTransition>
      <GeoSearch
        v-if="showCitySearch"
        :cities="getCities"
        :entities="getActiveStaticLayers"
        @clickClose="toggleCitySearch"
        @select="handleGeoSelection"
      />
      <GridLookupModal v-if="showGridLookupPopup" />
    </SlideYUpTransition>

    <ConsoleDetailsPopup
      :isSaveInProgress="isSaveInProgress"
      :dashboardData="dashboardData"
      :showSaveAsNewButton="showSaveAsNewButton"
      @clickSave="saveConsole"
      @clickUpdate="triggerUpdateConsole"
    />
    <ProgressView v-if="isRequestInProgress" />
    <SaveCompleted :show="showSavePopup" />
  </div>
</template>

<script>
import MetricStudioSidePanel from "@/components/metrics/views/MetricStudioSidePanel";
import MetricStudioBottomPanel from "@/components/metrics/views/MetricStudioBottomPanel";
import TopBar from "@/components/metrics/views/TopBar";
import MapView from "@/components/global/views/MapView";
import LegendView from "@/components/metrics/views/LegendView";
import ConsoleDetailsPopup from "@/components/metrics/modals/ConsoleDetailsPopup";
import ZoomControls from "@/components/metrics/views/ZoomControls";
import ComputedPropertyPopup from "@/components/metrics/modals/ComputedPropertyPopup";
import FilterManager from "@/components/metrics/containers/FilterManager";
import SaveCompleted from "@/components/metrics/modals/SaveCompleted";
// import CitySearch from "@/components/metrics/modals/CitySearch";
import GeoSearch from "@/modules/GeoSearch";
import ProgressView from "@/components/global/views/ProgressView";
// import DateFrequencyPicker from "@/components/datetimepicker/DateFrequencyPicker";
import { SlideYUpTransition } from "vue2-transitions";
import GridLookupModal from "@/components/metrics/modals/GridLookupModal";
import { trackConsoleSave } from "@/lib/analytics";

import { mapActions, mapGetters } from "vuex";
import City from "../../../models/City.model";

import { startTour } from "@/components/metrics/tours/LayerCreation.tour";

export default {
  components: {
    MetricStudioSidePanel,
    MapView,
    MetricStudioBottomPanel,
    LegendView,
    ConsoleDetailsPopup,
    ZoomControls,
    ComputedPropertyPopup,
    FilterManager,
    SaveCompleted,
    // CitySearch,
    GeoSearch,
    ProgressView,
    // DateFrequencyPicker,
    SlideYUpTransition,
    GridLookupModal,
    TopBar,
  },
  data() {
    return {
      showSavePopup: false,
      showCitySearch: false,
      dashboardData: null,
      showSaveAsNewButton: false,
    };
  },
  computed: {
    ...mapGetters("Metrics", [
      "getAggregations",
      "getComparisons",
      "getOperations",
    ]),
    ...mapGetters("Entities", ["getStaticEntities"]),
    // ...mapGetters("MetricStudio", ["getGridFilters", "showTimeLine"]),
    ...mapGetters("TimeManager", ["showBottomPanel"]),
    ...mapGetters("Time", ["getActiveTimeWindow"]),
    ...mapGetters("LayerManager", [
      "getActiveLayers",
      "getActiveEntity",
      "getActiveStaticLayers",
    ]),
    ...mapGetters("Map", [
      "getGeoData",
      "getLegend",
      "getMapLegends",
      "getMapStyle",
      "getPopupData",
    ]),
    ...mapGetters("Location", ["getCities", "getActiveCity"]),
    ...mapGetters("Popup", ["showConsoleDetailsPopup", "showGridLookupPopup"]),
    ...mapGetters("DataManager", ["isSaveInProgress", "isRequestInProgress"]),
    ...mapGetters("StudioManager", [
      "getConfig",
      "isFiltersVisible",
      "getGeoLevel",
      "getActiveGridLevel",
    ]),
    activePropertyName() {
      return this.getSelectedProperties && this.getSelectedProperties.length > 0
        ? this.getSelectedProperties.find(
            (item) => item.id === this.getActiveProperty
          ).name
        : "";
    },
    primaryLegend() {
      let primaryLegend = this.getMapLegends.find(
        (item) => item.type === "primary"
      );
      return primaryLegend ? primaryLegend.legend : [];
    },
    timeWindow: {
      get() {
        return this.getActiveTimeWindow;
      },
      set(value) {
        this.setActiveTimeWindow(value);
      },
    },
    mapData() {
      if (!(this.getGeoData && this.getGeoData.length > 0)) {
        return null;
      } else {
        return this.getGeoData[0];
      }
    },
  },
  created() {
    if (window.fcWidget) {
      window.fcWidget.hide();
    } else {
      setTimeout(() => {
        if (window.fcWidget) {
          window.fcWidget.hide();
        }
      }, 5000);
    }
  },
  methods: {
    ...mapActions("Metrics", ["fetchExtras"]),
    ...mapActions("Entities", ["fetchEntities", "fetchStaticEntities"]),
    ...mapActions("Map", ["setViewState"]),
    ...mapActions("Location", [
      "fetchCities",
      "setLocation",
      "fetchCityForLatLong",
      "setActiveCity",
    ]),
    ...mapActions("MetricStudio", ["fetchMetricData", "reset"]),
    ...mapActions("Popup", ["toggleConsoleDetailsPopup"]),
    ...mapActions("TimeManager", [
      // "setActiveTimeWindow",
      // "createTimePayload",
      // "createTimeFilterpayload",
      "toggleBottomPanel",
    ]),
    ...mapActions("Time", ["setActiveTimeWindow", "createTimeFilterpayload"]),
    ...mapActions("LayerManager", ["createPayload", "loadStaticEntity"]), // TODO: Improve this once static entites has been implemented in /entities
    ...mapActions("FilterManager", ["createEntityFilterPayload"]),
    ...mapActions("DataManager", [
      "fetchWidgets",
      "createConsole",
      "updateConsole",
    ]),
    ...mapActions("Datasources", ["fetchDataSources"]),
    ...mapActions("StudioManager", ["toggleFilterManager"]),
    ...mapActions("Dashboards", {
      resetDashboards: "reset",
    }),
    ...mapActions("Comments", ["postAllComments"]),
    handleGeoSelection(selection) {
      // console.log({ selection });
      if (selection.selection_type && selection.selection_type.mode) {
        switch (selection.selection_type.mode) {
          case "mapbox":
            this.setViewState({ zoom: selection.selection_type.zoom || 12 });
            this.setLocation([...selection.selection_type.location].reverse());
            this.setCityforLatLong(
              selection.selection_type.location,
              selection.bbox || [selection.center] || [
                  election.selection_type.location,
                ]
            );
            break;
          case "coordinates":
            this.setViewState({ zoom: selection.selection_type.zoom || 16 });
            this.setLocation([...selection.selection_type.location].reverse());
            this.setCityforLatLong(selection.selection_type.location, null);
            break;
          case "entity":
            // console.log({ selection });
            this.setViewState({ zoom: selection.selection_type.zoom || 16 });
            this.setLocation([...selection.geo]);
            this.setCityforLatLong([...selection.geo].reverse(), null);
            break;
        }
      }
    },
    async setCityforLatLong(latlong, bbox) {
      const city = await this.fetchCityForLatLong(latlong);
      const cityModel = new City({
        city_id: city.city_id,
        city: city.city,
        state: city.state || null,
        bbox: city.bbox || bbox || [latlong], // set the bbox to any of the mentioned
        lat_lon: latlong,
        zoom_level: 12,
      });
      if (city) {
        this.setActiveCity(cityModel);
      }
    },
    toggleConsoleDetailsPopupWithProps({ dashboardData, showSaveAsNewButton }) {
      this.showSaveAsNewButton = !!showSaveAsNewButton;
      if (dashboardData) {
        this.dashboardData = dashboardData;
      } else {
        this.dashboardData = null;
      }
      this.toggleConsoleDetailsPopup();
    },
    /**
     * Requests data for time change
     * Won't make requests for time series
     */
    async requestWidgetDataForTime() {
      // console.log('Request for Time');
      const payload = await this.createPayload({ event: "time_slice" });
      const time_filters = await this.createTimeFilterpayload();
      const categoricalWidget = await this.createEntityFilterPayload();

      if (categoricalWidget && categoricalWidget.widget_type) {
        this.fetchWidgets({
          payload: [...payload, categoricalWidget],
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      } else {
        this.fetchWidgets({
          payload,
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      }
    },
    /**
     * Requests data during a window change
     * Includes all single value widgets and geo, time and categories for active layer
     */
    async requestWidgetDataForWindow() {
      //console.log('Request for Window');
      const payload = await this.createPayload({
        active: false,
        event: "time_window",
      });

      const time_filters = await this.createTimeFilterpayload();
      const categoricalWidget = await this.createEntityFilterPayload();
      //console.log(timeFilters);
      if (categoricalWidget && categoricalWidget.widget_type) {
        this.fetchWidgets({
          payload: [...payload, categoricalWidget],
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      } else {
        this.fetchWidgets({
          payload,
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      }
    },
    /**
     * Requests data only for current categorical filters
     */
    async requestForCategoricalFilters() {
      //console.log('Request Categorical filters');
      const categoricalWidget = await this.createEntityFilterPayload();
      const time_filters = await this.createTimeFilterpayload();
      if (categoricalWidget && categoricalWidget.widget_type) {
        this.fetchWidgets({
          payload: [categoricalWidget],
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      } else {
        return;
      }
    },
    /**
     * Requests data when any filter is applied,
     * Data for single values, time and geo are requested
     */
    async requestForAppliedFilter() {
      //console.log("Request Applied Filter");
      const payload = await this.createPayload({ active: true });
      const categoricalWidget = await this.createEntityFilterPayload();
      const time_filters = await this.createTimeFilterpayload();

      if (categoricalWidget && categoricalWidget.widget_type) {
        this.fetchWidgets({
          payload: [...payload, categoricalWidget],
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      } else {
        this.fetchWidgets({
          payload: [...payload],
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      }
    },
    async requestForCityFilter() {
      // This doesn't get called when studio is initialized..
      // When this is called, proper grid_level is shown and map is positioned to proper city..

      const payload = await this.createPayload({ active: false });
      const categoricalWidget = await this.createEntityFilterPayload();
      const time_filters = await this.createTimeFilterpayload();

      if (!payload) {
        // If payload is not present no need to request for metrics
        return;
      }
      if (categoricalWidget && categoricalWidget.widget_type) {
        this.fetchWidgets({
          payload: [...payload, categoricalWidget],
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      } else {
        this.fetchWidgets({
          payload,
          filters: { time_filters, geo_level: this.getGeoLevel },
          temp: this.getConfig.isTempWidgets,
        });
      }
    },
    async requestForNewLayer() {
      //console.log('Request for new layer');
      try {
        const payload = await this.createPayload({ active: true });

        const time_filters = await this.createTimeFilterpayload();

        const categoricalWidget = await this.createEntityFilterPayload();
        //console.log(`Categorical`)

        if (payload) {
          if (categoricalWidget && categoricalWidget.widget_type) {
            payload.push(categoricalWidget);
          }
          if (
            time_filters &&
            ((time_filters.t_start && time_filters.t_end) ||
              time_filters.window)
          ) {
            this.fetchWidgets({
              payload,
              filters: { time_filters, geo_level: this.getGeoLevel },
              temp: this.getConfig.isTempWidgets,
            });
          } else {
            this.fetchWidgets({
              payload,
              temp: this.getConfig.isTempWidgets,
            });
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    async saveConsole({ name, description }) {
      const payload = await this.createPayload({ save: true });
      let meta = {
        grid_level: this.getActiveGridLevel,
      };
      let cityName = "unknown";
      // console.log({ bhat: this.getActiveCity.constructor === Object})
      if (
        this.getActiveCity &&
        Object.entries(this.getActiveCity).length !== 0
      ) {
        meta.city = Object.keys(this.getActiveCity).reduce((result, item) => {
          if (!this.getActiveCity[item]) return result;
          else
            return Object.assign(result, { [item]: this.getActiveCity[item] });
        }, {});
        cityName = this.getActiveCity.name;
      }

      const requestpayload = {
        name,
        description,
        map_style: this.getMapStyle,
        city: cityName,
        temp_metrics: payload,
        meta: meta,
      };
      this.createConsole(requestpayload).then(({ dashboard_id }) => {
        this.postAllComments({ dashboard_id });
        this.resetDashboards();
        trackConsoleSave();
        this.$router.push("/");
      });
    },
    async triggerUpdateConsole({ name, description }) {
      const dashboard_id = this.$route.query.dashboard_id;
      const payload = await this.createPayload({ save: true });
      let meta = {
        grid_level: this.getActiveGridLevel,
      };
      let cityName = "unknown";
      if (
        this.getActiveCity &&
        Object.entries(this.getActiveCity).length !== 0
      ) {
        meta.city = Object.keys(this.getActiveCity).reduce((result, item) => {
          if (!this.getActiveCity[item]) return result;
          else
            return Object.assign(result, { [item]: this.getActiveCity[item] });
        }, {});
        cityName = this.getActiveCity.name;
      }

      const requestpayload = {
        name,
        description,
        map_style: this.getMapStyle,
        city: cityName,
        temp_metrics: payload,
        meta: meta,
      };
      this.updateConsole({
        payload: requestpayload,
        dashboard_id,
      }).then(() => {
        this.resetDashboards();
        trackConsoleSave();
        this.$router.push("/");
      });
    },
    toggleCitySearch() {
      this.showCitySearch = !this.showCitySearch;
    },
  },
  watch: {
    async getActiveLayers(value) {
      this.requestForNewLayer();
    },
    async getGeoLevel(value) {
      this.requestWidgetDataForTime();
    },
    async getActiveCity(value) {
      this.requestForCityFilter();
    },
  },
  async mounted() {
    if (this.getConfig && !this.getConfig.isPublicView) {
      // this.reset();
      Promise.all([this.fetchExtras(), this.fetchDataSources()]);

      this.fetchStaticEntities().then(() => {
        // for each static entity load the entity list and create static entity layer (viz only)
        this.getStaticEntities.forEach((entity) =>
          this.loadStaticEntity(entity)
        );
      });
    } else {
      console.log("Not requesting anything from metric studio");
    }

    // this.fetchEntities().then(entities => {
    //   this.fetchCities(entities[0].id);
    // });

    if (this.getConfig.isTempWidgets && !this.getActiveCity) {
      setTimeout(this.toggleCitySearch, 500);
    }
    if (this.$route.fullPath === "/studio/edit") {
      startTour();
    }
  },
};
</script>

<style lang="scss" scoped>
.metric-studio {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: var(--color-secondary);

  .layout-grid {
    display: grid;
    grid-template-rows: 50px 1fr;
    height: 100%;

    .content-layout {
      background: var(--color-primary);
      display: grid;
      max-width: 100%;
      grid-template-columns: 350px 1fr;
      min-height: 0; /* NEW */
      min-width: 0;
      background: var(--color-default-background);

      .control-bar {
        z-index: 998;
        display: flex;
        flex-direction: row;
        align-items: center;
        background: var(--color-secondary);
        border-bottom: 1px solid var(--border-color);
        border-left: 1px solid var(--border-color);
      }

      .display-area {
        overflow: hidden;
        width: 100%;
        position: relative;

        .toggle-button {
          font-size: 24px;
          width: 25px;
          height: 25px;
          color: var(--font-color);
          padding: 10px;
          border-radius: 50%;
          position: absolute;
          background: var(--color-secondary);
          z-index: 999;
          transition: 0.3s all ease-in-out;

          &:hover {
            color: var(--color-primary);
            cursor: pointer;
          }

          &:active {
            transform: scale(0.8);
          }

          &.timeline {
            bottom: 12px;
            left: 0;
            right: 0;
            margin: 0 auto;
          }

          &.filters {
            top: 12px;
            right: 12px;
          }
          &.legend {
            top: 12px;
            right: 12px;
          }

          &.hide {
            transform: scale(0);
          }
        }
      }
    }
  }
}
</style>
